import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Award, Users, GraduationCap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocalizedContent } from "@/hooks/use-localized-content";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface Certificate {
  title: string;
  description: string;
  image_url: string;
}

interface StaffMember {
  id: string;
  name: string;
  photo_url: string | null;
  bio: string | null;
  specialties: string | null;
  certificates: Certificate[];
}

const CertificatesPage = () => {
  const { t } = useTranslation();
  const { c } = useLocalizedContent();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("staff")
        .select("id, name, photo_url, bio, specialties, certificates")
        .eq("is_active", true)
        .order("name");
      const list = (data || []).map((s: any) => ({
        ...s,
        certificates: Array.isArray(s.certificates) ? s.certificates : [],
      })) as StaffMember[];
      setStaff(list);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-4 py-1.5 text-sm font-medium mb-4">
            <GraduationCap size={16} />
            {c("cert_badge", "_none_", "Formação & Qualificações")}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
            {c("cert_title", "_none_", "Certificados dos Colaboradores")}
          </h1>
          <p className="text-lg text-muted-foreground">
            {c(
              "cert_subtitle",
              "_none_",
              "Conheça a formação e as certificações da nossa equipa, dedicada a oferecer serviços de excelência com profissionalismo e cuidado."
            )}
          </p>
        </div>
      </section>

      {/* Staff & Certificates */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          {loading && (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && staff.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <Users size={48} className="mx-auto mb-3 opacity-40" />
              <p>{c("cert_empty", "_none_", "Ainda não há certificados publicados.")}</p>
            </div>
          )}

          {!loading &&
            staff.map((member) => (
              <div key={member.id} className="mb-20">
                {/* Staff header */}
                <div className="flex flex-col items-center text-center mb-10">
                  {member.photo_url ? (
                    <img
                      src={member.photo_url}
                      alt={member.name}
                      loading="lazy"
                      className="w-32 h-32 rounded-full object-cover border-4 border-primary/20 shadow-lg mb-4"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center border-4 border-primary/20 mb-4">
                      <Users size={40} className="text-primary" />
                    </div>
                  )}
                  <h2 className="font-display text-3xl font-bold text-foreground">{member.name}</h2>
                  {member.specialties && (
                    <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                      {member.specialties
                        .split(",")
                        .map((c) => c.trim())
                        .filter(Boolean)
                        .map((cat) => (
                          <span
                            key={cat}
                            className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full"
                          >
                            {cat}
                          </span>
                        ))}
                    </div>
                  )}
                  {member.bio && (
                    <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">{member.bio}</p>
                  )}
                </div>

                {/* Certificates alternating layout */}
                {member.certificates.length === 0 ? (
                  <p className="text-center text-muted-foreground italic text-sm">
                    {c("cert_none_yet", "_none_", "Sem certificados publicados para este colaborador.")}
                  </p>
                ) : (
                  <div className="space-y-12">
                    {member.certificates.map((cert, idx) => {
                      const imageLeft = idx % 2 === 0;
                      return (
                        <div
                          key={idx}
                          className="grid md:grid-cols-2 gap-8 items-center rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow"
                        >
                          {/* Image */}
                          <div className={imageLeft ? "md:order-1" : "md:order-2"}>
                            {cert.image_url ? (
                              <img
                                src={cert.image_url}
                                alt={cert.title}
                                loading="lazy"
                                className="w-full h-auto max-h-[420px] object-contain rounded-xl bg-muted/30"
                              />
                            ) : (
                              <div className="w-full aspect-[4/3] rounded-xl bg-muted/30 flex items-center justify-center">
                                <Award size={48} className="text-muted-foreground/40" />
                              </div>
                            )}
                          </div>

                          {/* Text */}
                          <div className={imageLeft ? "md:order-2" : "md:order-1"}>
                            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold mb-3">
                              <Award size={14} />
                              {c("cert_label", "_none_", "Certificado")} #{idx + 1}
                            </div>
                            <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
                              {cert.title}
                            </h3>
                            {cert.description && (
                              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                                {cert.description}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

          {/* CTA */}
          <div className="text-center mt-12 border-t border-border pt-12">
            <h3 className="font-display text-2xl font-bold text-foreground mb-3">
              {c("cert_cta_title", "_none_", "Confie em profissionais qualificados")}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              {c(
                "cert_cta_subtitle",
                "_none_",
                "Todos os nossos colaboradores são formados e certificados para lhe garantir a melhor experiência."
              )}
            </p>
            <Button asChild size="lg" className="rounded-full">
              <Link to="/#agendamento">{c("cert_cta_button", "nav.book", "Agendar")}</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CertificatesPage;
